<template>
  <chatflow-page>
    <chatflow-section-card title="Kanal ekle" class="mb-4 cf-card" elevation="2">
      <v-row dense align="center">
        <v-col cols="12" sm="3">
          <v-select
            v-model="channelType"
            :items="channelTypeOptions"
            item-text="text"
            item-value="value"
            label="Kanal türü"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field v-model="form.name" label="Kanal adı" outlined dense hide-details />
        </v-col>

        <v-col v-if="channelType === 'telegram'" cols="12" sm="3">
          <v-text-field
            v-model="form.botToken"
            label="Bot token"
            type="password"
            name="telegram-bot-token"
            autocomplete="new-password"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col v-if="channelType === 'whatsapp'" cols="12" sm="3">
          <v-text-field
            v-model="form.phoneNumberId"
            label="Phone Number ID"
            outlined
            dense
            hide-details
          />
        </v-col>

        <v-col v-if="channelType === 'whatsapp'" cols="12" sm="3">
          <v-text-field
            v-model="form.accessToken"
            label="Access token"
            type="password"
            outlined
            dense
            hide-details
          />
        </v-col>

        <v-col v-if="channelType === 'web'" cols="12" sm="3">
          <v-text-field
            v-model="form.allowedOriginsText"
            label="Allowed origins (virgül ile)"
            placeholder="https://site.com,https://app.site.com"
            outlined
            dense
            hide-details
          />
        </v-col>

        <v-col cols="12" sm="2">
          <v-select
            v-model="form.flowId"
            :items="flows"
            item-text="name"
            item-value="id"
            label="Varsayılan akış"
            outlined
            dense
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="1">
          <v-btn color="primary" depressed :loading="saving" block @click="saveChannel">Ekle</v-btn>
        </v-col>
      </v-row>
    </chatflow-section-card>

    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="channels"
      class="shadow-xl cf-card"
      elevation="2"
    >
      <template v-slot:top>
        <v-toolbar flat color="white" class="px-2">
          <v-toolbar-title class="text-h6 font-weight-medium">Kanallar</v-toolbar-title>
          <v-spacer />
          <v-btn text small color="primary" @click="load">
            <v-icon left small>refresh</v-icon>
            Yenile
          </v-btn>
        </v-toolbar>
        <v-divider />
      </template>
      <template v-slot:item.bot="{ item }">
        {{ channelInfo(item) }}
      </template>
      <template v-slot:item.flowId="{ item }">
        {{ flowName(item.flowId) }}
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn icon small @click="removeChannel(item.id)">
          <v-icon small>delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-snackbar v-model="snack" :color="snackColor">{{ snackText }}</v-snackbar>
  </chatflow-page>
</template>

<script>
import ChatflowApi from "../api";
import ChatflowAuthMixin from "../mixins/auth";
import ChatflowPage from "../components/ChatflowPage.vue";
import ChatflowSectionCard from "../components/ChatflowSectionCard.vue";

export default {
  components: { ChatflowPage, ChatflowSectionCard },
  mixins: [ChatflowAuthMixin("Chatflow Channels")],
  data: () => ({
    loading: false,
    saving: false,
    channels: [],
    flows: [],
    channelType: "telegram",
    channelTypeOptions: [
      { text: "Telegram", value: "telegram" },
      { text: "WhatsApp", value: "whatsapp" },
      { text: "Web", value: "web" },
    ],
    form: {
      name: "",
      flowId: null,
      botToken: "",
      phoneNumberId: "",
      accessToken: "",
      allowedOriginsText: "",
    },
    headers: [
      { text: "Ad", value: "name" },
      { text: "Tür", value: "type" },
      { text: "Bot", value: "bot" },
      { text: "Akış", value: "flowId" },
      { text: "", value: "action", sortable: false, width: 60 },
    ],
    snack: false,
    snackText: "",
    snackColor: "success",
  }),
  created() {
    this.load();
  },
  methods: {
    flowName(id) {
      if (!id) return "—";
      const f = this.flows.find((x) => x.id === id);
      return f ? f.name : id;
    },
    async load() {
      this.loading = true;
      try {
        const [channels, flows] = await Promise.all([
          ChatflowApi.getChannels(),
          ChatflowApi.getFlows(),
        ]);
        this.channels = channels;
        this.flows = flows;
      } catch (e) {
        this.notify(e.response?.data?.message || "Kanallar yüklenemedi", "error");
      }
      this.loading = false;
    },
    channelInfo(item) {
      if (item.type === "telegram") {
        return item.credentials && item.credentials.botUsername
          ? `@${item.credentials.botUsername}`
          : "—";
      }
      if (item.type === "whatsapp") {
        return item.credentials?.phoneNumberId || "—";
      }
      if (item.type === "web") {
        const count = Array.isArray(item.credentials?.allowedOrigins)
          ? item.credentials.allowedOrigins.length
          : 0;
        return count ? `${count} origin` : "public";
      }
      return "—";
    },
    resetForm() {
      this.form = {
        name: "",
        flowId: null,
        botToken: "",
        phoneNumberId: "",
        accessToken: "",
        allowedOriginsText: "",
      };
    },
    parseOrigins() {
      return String(this.form.allowedOriginsText || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    },
    async saveChannel() {
      if (!this.form.name) {
        this.notify("Kanal adı gerekli", "warning");
        return;
      }
      if (this.channelType === "telegram" && !this.form.botToken) {
        this.notify("Telegram için bot token gerekli", "warning");
        return;
      }
      if (this.channelType === "whatsapp" && !this.form.phoneNumberId) {
        this.notify("WhatsApp için Phone Number ID gerekli", "warning");
        return;
      }
      if (!this.form.flowId) {
        this.notify("Varsayılan akış seçmelisin", "warning");
        return;
      }
      this.saving = true;
      try {
        if (this.channelType === "telegram") {
          const ch = await ChatflowApi.createTelegramChannel({
            name: this.form.name.trim(),
            botToken: this.form.botToken.trim(),
            flowId: this.form.flowId || null,
          });
          const reg = await ChatflowApi.registerTelegramWebhook(ch.id);
          this.notify(`Telegram kanal eklendi. Webhook: ${reg.webhookUrl || "OK"}`, "success");
        } else if (this.channelType === "whatsapp") {
          const ch = await ChatflowApi.createWhatsAppChannel({
            name: this.form.name.trim(),
            flowId: this.form.flowId || null,
            phoneNumberId: this.form.phoneNumberId.trim(),
            accessToken: this.form.accessToken.trim(),
          });
          this.notify(
            `WhatsApp kanal eklendi. Callback: ${ch.webhookUrl || "N/A"} | Verify token: ${ch.verifyToken || "N/A"}`,
            "success"
          );
        } else if (this.channelType === "web") {
          const ch = await ChatflowApi.createWebChannel({
            name: this.form.name.trim(),
            flowId: this.form.flowId || null,
            allowedOrigins: this.parseOrigins(),
          });
          this.notify(`Web kanal eklendi. Endpoint: ${ch.webchatEndpoint || "N/A"}`, "success");
        }
        this.resetForm();
        await this.load();
      } catch (e) {
        this.notify(e.response?.data?.message || "Kayıt başarısız", "error");
      }
      this.saving = false;
    },
    async removeChannel(id) {
      if (!confirm("Kanalı sil?")) return;
      try {
        await ChatflowApi.deleteChannel(id);
        await this.load();
      } catch (e) {
        this.notify(e.response?.data?.message || "Silinemedi", "error");
      }
    },
    notify(text, color = "success") {
      this.snackText = text;
      this.snackColor = color;
      this.snack = true;
    },
  },
};
</script>

<style scoped>
.cf-card {
  border-radius: 8px;
}
</style>
